import type { TFunction } from 'i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';

/**
 * The import finding codes insuranceservice reports (SDD-020 §7.4, §7.6; prompt 28 Decision 7),
 * mirrored in `insurance:import.findings.*`. The service publishes its catalog in
 * `api/exports/import-findings.json`; a parity test fails when a code exists on one side only.
 */
export const IMPORT_FINDING_CODES = [
  'ALREADY_EXISTS',
  'CELL_NOT_ALLOWED',
  'CELL_NOT_A_BOOLEAN',
  'CELL_NOT_A_DATE',
  'CELL_NOT_A_NUMBER',
  'CELL_PATTERN',
  'CELL_REQUIRED',
  'DUPLICATE_IN_FILE',
  'FILE_TOO_LARGE',
  'FILE_UNREADABLE',
  'MISSING_REQUIRED_COLUMN',
  'NOT_FOUND',
  'PARENT_NOT_ACTIVE',
  'PARTNER_ID_OR_DRAFT',
  'PARTNER_ROLES_VIOLATION',
  'ROW_REFUSED',
  'SCHEMA_VIOLATION',
  'TARIF_NOT_ALLOWED',
  'TEMPLATE_VERSION_DIFFERS',
  'TOO_MANY_ROWS',
  'UNKNOWN_COLUMN',
  'VALIDATION',
] as const;

export type ImportFindingCode = (typeof IMPORT_FINDING_CODES)[number];

export type ImportFinding = InstanceApi.ImportFinding;

const isKnown = (code: string | undefined): code is ImportFindingCode =>
  (IMPORT_FINDING_CODES as readonly string[]).includes(code ?? '');

/**
 * The sentence for a finding. The service already words it in the request's language with the
 * cell's details (`{pattern}`, `{allowed}`), so its `message` wins; the console's catalog speaks
 * for a code whose message is empty, with the columns a file-level finding names.
 */
export function findingMessage(t: TFunction<'insurance'>, finding: ImportFinding): string {
  const message = finding.message ?? '';
  if (message !== '') return message;
  if (!isKnown(finding.code)) return finding.code ?? '';
  return t(`import.findings.${finding.code}`, {
    keys: (finding.keys ?? []).join(', '),
    allowed: '',
    pattern: '',
    first: '',
    format: '',
    detail: finding.value ?? '',
    template: '',
    version: '',
    max: '',
  });
}

/** An error stops the row; a warning (a template of another version) only informs. */
export function isWarning(finding: ImportFinding): boolean {
  return finding.severity === 'WARNING';
}
